import { useEffect, useMemo, useState } from "react";
import { Plus, Users, Wallet, Loader2, Trash2, AlertTriangle, ShieldAlert, ShieldCheck, Clock3 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { SocioScreeningDialog } from "@/components/socios/SocioScreeningDialog";
import { toast } from "sonner";

const TIPO_PERSONA = [
  { value: "fisica_nacional", label: "Persona física nacional" },
  { value: "fisica_extranjera", label: "Persona física extranjera" },
  { value: "moral_nacional", label: "Persona moral nacional" },
  { value: "moral_extranjera", label: "Persona moral extranjera" },
];

const TIPO_APORTACION = [
  { value: "aportacion_inicial", label: "Aportación inicial (capital de arranque)" },
  { value: "aumento_capital", label: "Aumento de capital" },
];

const FORMAS = [
  { value: "transferencia", label: "Transferencia (Bancos 1102)" },
  { value: "efectivo", label: "Efectivo (Caja 1101)" },
];

const TIPO_PERSONA_LABEL = TIPO_PERSONA.reduce((a, o) => ({ ...a, [o.value]: o.label }), {});

// Distintivo del cotejo de listas del socio. Lee `screening_ref` sellado por el backend
// (socios_screening.py) — nunca recalcula ni reconsulta.
function BadgeListas({ socio, onVer }) {
  const ref = socio?.screening_ref;
  if (!ref) {
    return (
      <Badge variant="outline" className="border-slate-300 text-slate-500"
             data-testid={`listas-badge-${socio.id.slice(0, 6)}`}>
        Sin cotejar
      </Badge>
    );
  }
  const crit = ref.criticidad || "ninguna";
  const pendiente = !!ref.screening_pendiente;
  const cfg = pendiente
    ? { cls: "border-amber-300 bg-amber-50 text-amber-800", txt: "Pendiente", Icon: Clock3 }
    : crit === "alta"
      ? { cls: "border-rose-300 bg-rose-50 text-rose-800", txt: "Coincidencia", Icon: ShieldAlert }
      : crit === "media"
        ? { cls: "border-amber-300 bg-amber-50 text-amber-800", txt: "Parcial", Icon: ShieldAlert }
        : { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", txt: "Sin coincidencia", Icon: ShieldCheck };
  const { Icon } = cfg;
  return (
    <button type="button" onClick={() => onVer(socio.id)}
            className="transition-opacity hover:opacity-75"
            data-testid={`listas-badge-${socio.id.slice(0, 6)}`}>
      <Badge variant="outline" className={`gap-1 ${cfg.cls}`}>
        <Icon className="h-3 w-3" /> {cfg.txt}
      </Badge>
    </button>
  );
}

function DialogNuevoSocio({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tipo_persona: "fisica_nacional",
    nombre_o_razon_social: "",
    rfc: "",
    participacion_pct: 0,
    fecha_ingreso: new Date().toISOString().slice(0, 10),
    notas: "",
    // Eslabón cero: aporte inicial opcional que se dispara al alta del socio.
    // Reutiliza el motor de aportaciones existente (Dr 1101/1102 · Cr 3101). Todo el bloque
    // es OPCIONAL: si `capturar_aporte` es false o `aporte_monto` <= 0, se crea el socio sin
    // aportación (comportamiento actual).
    capturar_aporte: false,
    aporte_monto: 0,
    aporte_forma: "transferencia",
    aporte_banco: "",
    aporte_fecha: new Date().toISOString().slice(0, 10),
    aporte_notas: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.nombre_o_razon_social.trim()) {
      toast.error("Captura el nombre o razón social del socio.");
      return;
    }
    if (form.capturar_aporte && !(Number(form.aporte_monto) > 0)) {
      toast.error("Si marcas 'aporta capital al momento del alta', el monto debe ser mayor a cero.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tipo_persona: form.tipo_persona,
        nombre_o_razon_social: form.nombre_o_razon_social,
        rfc: form.rfc,
        participacion_pct: Number(form.participacion_pct) || 0,
        fecha_ingreso: form.fecha_ingreso,
        notas: form.notas,
      };
      if (form.capturar_aporte && Number(form.aporte_monto) > 0) {
        payload.aporte_inicial = {
          monto: Number(form.aporte_monto),
          fecha: form.aporte_fecha || form.fecha_ingreso,
          forma: form.aporte_forma,
          banco_receptor: form.aporte_forma === "transferencia" ? form.aporte_banco : null,
          notas: form.aporte_notas,
        };
      }
      const r = await api.post("/finanzas/socios", payload);
      if (r.data?.aportacion) {
        toast.success(`Socio registrado con aportación inicial. Póliza ${r.data.poliza?.referencia || ""}`);
      } else {
        toast.success("Socio registrado");
      }
      setOpen(false);
      setForm({
        ...form,
        nombre_o_razon_social: "", rfc: "", participacion_pct: 0, notas: "",
        capturar_aporte: false, aporte_monto: 0, aporte_banco: "", aporte_notas: "",
      });
      onCreated?.(r.data?.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-nuevo-socio">
          <Plus className="mr-2 h-4 w-4" /> Nuevo socio
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Alta de socio</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Tipo de persona</Label>
            <Select value={form.tipo_persona} onValueChange={set("tipo_persona")}>
              <SelectTrigger data-testid="socio-tipo-persona"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPO_PERSONA.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre o razón social *</Label>
            <Input
              data-testid="socio-nombre"
              value={form.nombre_o_razon_social}
              onChange={set("nombre_o_razon_social")}
              placeholder="Ej. Juan Pérez López / Sociedad Ejemplo S.A. de C.V."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>RFC</Label>
              <Input data-testid="socio-rfc" value={form.rfc} onChange={set("rfc")} placeholder="RFC" />
            </div>
            <div>
              <Label>% Participación</Label>
              <Input
                data-testid="socio-pct"
                type="number" min="0" max="100" step="0.01"
                value={form.participacion_pct}
                onChange={set("participacion_pct")}
              />
            </div>
          </div>
          <div>
            <Label>Fecha de ingreso</Label>
            <Input
              data-testid="socio-fecha"
              type="date"
              value={form.fecha_ingreso}
              onChange={set("fecha_ingreso")}
            />
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea data-testid="socio-notas" value={form.notas} onChange={set("notas")} rows={2} />
          </div>

          {/* Eslabón cero: sección opcional para capturar el aporte inicial al momento del alta.
              Reutiliza el motor de aportaciones (Dr 1101/1102 · Cr 3101). Si no se marca,
              el socio se crea sin póliza (comportamiento previo). */}
          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="socio-toggle-aporte"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                checked={form.capturar_aporte}
                onChange={(e) => setForm({ ...form, capturar_aporte: e.target.checked })}
              />
              <span className="text-sm">
                <span className="font-medium text-slate-900">¿Aporta capital al momento del alta?</span>
                <span className="ml-1 text-slate-500">
                  Opcional. Si lo marcas, se generará la aportación inicial y su póliza contable automáticamente.
                </span>
              </span>
            </label>

            {form.capturar_aporte && (
              <div className="mt-3 grid gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Monto (MXN) *</Label>
                    <MoneyInput
                      data-testid="socio-aporte-monto"
                      value={form.aporte_monto}
                      onChange={set("aporte_monto")}
                    />
                  </div>
                  <div>
                    <Label>Fecha del aporte</Label>
                    <Input
                      data-testid="socio-aporte-fecha"
                      type="date"
                      value={form.aporte_fecha}
                      onChange={set("aporte_fecha")}
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Puedes usar una fecha anterior (aporte histórico) para conciliar tesorería.
                    </p>
                  </div>
                </div>
                <div>
                  <Label>Forma</Label>
                  <Select value={form.aporte_forma} onValueChange={set("aporte_forma")}>
                    <SelectTrigger data-testid="socio-aporte-forma"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FORMAS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.aporte_forma === "transferencia" && (
                  <div>
                    <Label>Banco receptor</Label>
                    <Input
                      data-testid="socio-aporte-banco"
                      value={form.aporte_banco}
                      onChange={set("aporte_banco")}
                      placeholder="Ej. BBVA, Santander, HSBC…"
                    />
                  </div>
                )}
                <div>
                  <Label>Notas del aporte</Label>
                  <Textarea
                    data-testid="socio-aporte-notas"
                    value={form.aporte_notas}
                    onChange={set("aporte_notas")}
                    rows={2}
                    placeholder="Ej. Aportación de constitución según acta N°..."
                  />
                </div>
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-900">
                  <div className="font-medium">Póliza automática que se generará:</div>
                  <div className="mt-1 font-mono">
                    Dr <b>{form.aporte_forma === "efectivo" ? "1101 Caja" : "1102 Bancos"}</b> · Cr <b>3101 Capital social pagado</b>
                    {" — "}${Number(form.aporte_monto || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="socio-guardar-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar socio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogNuevaAportacion({ socios, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    socio_id: "",
    monto: 0,
    fecha: new Date().toISOString().slice(0, 10),
    tipo: "aumento_capital",
    forma: "transferencia",
    banco_receptor: "",
    notas: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.socio_id) { toast.error("Selecciona el socio."); return; }
    if (!(form.monto > 0)) { toast.error("El monto debe ser mayor a cero."); return; }
    setSaving(true);
    try {
      const r = await api.post("/finanzas/aportaciones", { ...form, monto: Number(form.monto) });
      toast.success(`Aportación registrada. Póliza ${r.data.poliza?.referencia || ""}`);
      setOpen(false);
      setForm({ ...form, monto: 0, notas: "" });
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const activos = socios.filter((s) => s.activo !== false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-nueva-aportacion" disabled={activos.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> Registrar aportación
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva aportación de capital</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Socio *</Label>
            <Select value={form.socio_id} onValueChange={set("socio_id")}>
              <SelectTrigger data-testid="apo-socio"><SelectValue placeholder="Selecciona socio…" /></SelectTrigger>
              <SelectContent>
                {activos.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre_o_razon_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de aportación</Label>
              <Select value={form.tipo} onValueChange={set("tipo")}>
                <SelectTrigger data-testid="apo-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_APORTACION.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma</Label>
              <Select value={form.forma} onValueChange={set("forma")}>
                <SelectTrigger data-testid="apo-forma"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMAS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Monto (MXN) *</Label>
              <MoneyInput
                data-testid="apo-monto"
                value={form.monto} onChange={set("monto")}
              />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input data-testid="apo-fecha" type="date" value={form.fecha} onChange={set("fecha")} />
            </div>
          </div>
          {form.forma === "transferencia" && (
            <div>
              <Label>Banco receptor</Label>
              <Input
                data-testid="apo-banco"
                value={form.banco_receptor} onChange={set("banco_receptor")}
                placeholder="Ej. BBVA, Santander, HSBC…"
              />
            </div>
          )}
          <div>
            <Label>Notas</Label>
            <Textarea data-testid="apo-notas" value={form.notas} onChange={set("notas")} rows={2} />
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-900">
            <div className="font-medium">Póliza automática que se generará:</div>
            <div className="mt-1 font-mono">
              Dr <b>{form.forma === "efectivo" ? "1101 Caja" : "1102 Bancos"}</b> · Cr <b>3101 Capital social pagado</b>
              {" — "}${Number(form.monto || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="apo-guardar-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar aportación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SociosYAportaciones() {
  const [socios, setSocios] = useState([]);
  const [aportaciones, setAportaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screeningSocioId, setScreeningSocioId] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [rs, ra] = await Promise.all([
        api.get("/finanzas/socios"),
        api.get("/finanzas/aportaciones"),
      ]);
      setSocios(rs.data.socios || []);
      setAportaciones(ra.data.aportaciones || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const eliminarSocio = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar al socio ${nombre}? Solo se permite si no tiene aportaciones registradas.`)) return;
    try {
      await api.delete(`/finanzas/socios/${id}`);
      toast.success("Socio eliminado");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totalCapital = useMemo(
    () => aportaciones.reduce((s, a) => s + (a.monto || 0), 0),
    [aportaciones]
  );
  const totalPct = useMemo(
    () => socios.filter((s) => s.activo !== false).reduce((s, x) => s + (x.participacion_pct || 0), 0),
    [socios]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Socios y aportaciones de capital"
        subtitle="Registro de socios y aportaciones. Cada aportación genera una póliza automática Dr Caja/Bancos · Cr 3101 Capital social pagado."
      />

      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Socios activos</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-socios-activos">
              {socios.filter((s) => s.activo !== false).length}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Capital pagado (histórico)</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-capital-pagado">
              {mxn(totalCapital)}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${Math.abs(totalPct - 100) < 0.01 ? "border-emerald-200 bg-emerald-50" : totalPct > 0 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
            <div className="text-xs uppercase tracking-wide text-slate-500">% participación total</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="stat-pct-total">
              {totalPct.toFixed(2)}%
            </div>
            {totalPct > 0 && Math.abs(totalPct - 100) >= 0.01 && (
              <div className="mt-0.5 text-[11px] text-amber-800">
                El total no cuadra en 100%. Revisa los % de participación.
              </div>
            )}
          </div>
        </div>

        <Tabs defaultValue="socios">
          <TabsList>
            <TabsTrigger value="socios" data-testid="tab-socios">Socios</TabsTrigger>
            <TabsTrigger value="aportaciones" data-testid="tab-aportaciones">Aportaciones</TabsTrigger>
          </TabsList>

          <TabsContent value="socios" className="mt-4">
            <div className="mb-3 flex justify-end">
              <DialogNuevoSocio onCreated={(id) => { cargar(); if (id) setScreeningSocioId(id); }} />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre / Razón social</TableHead>
                    <TableHead>RFC</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Listas</TableHead>
                    <TableHead className="text-right">% participación</TableHead>
                    <TableHead>Fecha ingreso</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell></TableRow>
                  )}
                  {!loading && socios.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      Sin socios registrados. Da de alta el primero para poder registrar aportaciones.
                    </TableCell></TableRow>
                  )}
                  {socios.map((s) => (
                    <TableRow key={s.id} data-testid={`row-socio-${s.id.slice(0,6)}`}>
                      <TableCell className="font-medium">{s.nombre_o_razon_social}</TableCell>
                      <TableCell className="font-mono text-xs">{s.rfc || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{TIPO_PERSONA_LABEL[s.tipo_persona] || s.tipo_persona}</Badge>
                      </TableCell>
                      <TableCell>
                        <BadgeListas socio={s} onVer={setScreeningSocioId} />
                      </TableCell>
                      <TableCell className="text-right">{(s.participacion_pct || 0).toFixed(2)}%</TableCell>
                      <TableCell>{s.fecha_ingreso || "—"}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => eliminarSocio(s.id, s.nombre_o_razon_social)}
                          data-testid={`btn-eliminar-socio-${s.id.slice(0,6)}`}
                        >
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="aportaciones" className="mt-4">
            <div className="mb-3 flex justify-end">
              <DialogNuevaAportacion socios={socios} onCreated={cargar} />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Socio</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Forma</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Póliza</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell></TableRow>
                  )}
                  {!loading && aportaciones.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      Sin aportaciones registradas todavía.
                    </TableCell></TableRow>
                  )}
                  {aportaciones.map((a) => (
                    <TableRow key={a.id} data-testid={`row-apo-${a.id.slice(0,6)}`}>
                      <TableCell>{a.fecha}</TableCell>
                      <TableCell className="font-medium">{a.socio_nombre}</TableCell>
                      <TableCell><Badge variant="outline">{a.tipo}</Badge></TableCell>
                      <TableCell>{a.forma}</TableCell>
                      <TableCell className="text-right font-medium">{mxn(a.monto)}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{a.poliza_ref}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <SocioScreeningDialog
        socioId={screeningSocioId}
        open={!!screeningSocioId}
        onOpenChange={(v) => { if (!v) setScreeningSocioId(null); }}
      />
    </div>
  );
}
